import './styles.scss'

import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Divider } from 'antd'

import B2becSpinner from '@/components/B2becSpinner'
import { useAppDispatch, useAppSelector } from '@/store/hook'
import {
  getDeliveryData,
  getIsLoadingCheckout,
  getOrderData,
} from '@/store/selectors/checkoutSelector'
import { trackCompanyInfo, trackTransaction } from '@/store/slices/adobeAnalyticSlice'
import { selectCustomerAddress, selectCustomerFetchStatus } from '@/store/slices/customerSlice'
import useAdobeAnalysis from '@/hooks/useAdobeAnalysis'
import useBreadcrumbs from '@/hooks/useBreadcrumbs'
import useDocumentTitle from '@/hooks/useDocumentTitle'
import { ACTION_STATUS } from '@/libs/constants'
import { PAGE_TYPES } from '@/libs/constants/adobeAnalytics'
import { analyzeTransaction } from '@/libs/utils/adobeAnalysis'
import { linkGenerator } from '@/libs/utils/language'

import BottomSectionComponent from './BottomSection'
import MiddleSectionComponent from './MiddleSection'
import UpperSectionComponent from './UpperSection'

const OrderConfirmationPage = ({ breadcrumbs }) => {
  useDocumentTitle('pageTitle.orderConfirmation')
  useBreadcrumbs(breadcrumbs)

  useAdobeAnalysis(null, {
    pageName: 'order confirmation',
    pageType: PAGE_TYPES.CONFIRMATION,
    pageId: '4',
  })

  const { t } = useTranslation()
  const navigate = useNavigate()

  const orderData = useAppSelector(getOrderData)
  const deliveryData = useAppSelector(getDeliveryData)
  const isCheckoutLoading = useAppSelector(getIsLoadingCheckout)
  const customerAddress = useAppSelector(selectCustomerAddress)
  const customerFetchStatus = useAppSelector(selectCustomerFetchStatus)

  const { salesOrderNumber } = orderData
  const dispatch = useAppDispatch()

  // analyze transaction
  useEffect(() => {
    if (orderData && deliveryData && salesOrderNumber) {
      const transaction = analyzeTransaction({ orderData, deliveryData })

      dispatch(trackTransaction(transaction))
    }
    if (customerFetchStatus !== ACTION_STATUS.DEFAULT) {
      const companyName = `${customerAddress?.name || ''} ${customerAddress?.name2 || ''}`.trim()

      const companyID = customerAddress?.partnerNumber || ''

      dispatch(
        trackCompanyInfo({
          companyName,
          companyID,
        }),
      )
    }
  }, [
    dispatch,
    orderData,
    deliveryData,
    salesOrderNumber,
    customerAddress,
    customerFetchStatus,
  ])

  useEffect(() => {
    if (!salesOrderNumber) {
      navigate(linkGenerator('/my-workspace'))
    }
  }, [salesOrderNumber, navigate])

  return (
    <div className="order-confirm-wrapper">
      <B2becSpinner isLoading={isCheckoutLoading} />
      <h1 className="order-confirm__header">
        {t('checkout.confirmOrder.thankForYourOrder')}!
      </h1>
      <div className="order-confirm__main">
        <UpperSectionComponent salesOrderNumber={salesOrderNumber} />
        <MiddleSectionComponent />
        <Divider className="order-confirm__divider" />
        <BottomSectionComponent />
      </div>
    </div>
  )
}

export default OrderConfirmationPage
